import {createSelector} from 'reselect';
import get from "lodash/get";
import find from "lodash/find";
import reduce from "lodash/reduce";
import type {
  SecurityStandardGroup,
  SecurityStandard,
  SecurityStandardGuideline, SecurityStandardGuidelineRelationships,
  SecurityStandardVariant
} from "amn/model/standards/SecurityStandards";
import type {LookupTable} from "amn/model/common/Divers";
import memoize from "lodash/memoize";
import type {DataStatus, ListDataState} from "../../reducers/common";
import filter from "lodash/filter";
import includes from "lodash/includes";
import {isGuidelineWithSubGuidelines} from "amn/model/standards/SecurityStandards";
import {listToLookupTable} from "amn/model/common/Divers";
import {getAvailableStandardGroups, getAvailableStandards} from "amn/backend/local/standards/SecurityStandardsData";
import some from "lodash/some";
import map from "lodash/map";
import type {I18nText} from "amn/ui/i18n/i18nText.types";

export type SecurityStandardsGroupSection = {
  id: string;
  name?: I18nText;
  description?: I18nText;
  icon: string;
  standards: SecurityStandard[];
  fallback?: boolean;
}

export const selectSecurityStandardsState = (state) => get(state, 'securityStandards');

export const selectSecurityStandards = createSelector(
  selectSecurityStandardsState,
  (state): LookupTable<SecurityStandard> => get(state, 'entities')
);
export const selectSecurityStandardsDataStatus = createSelector(
  selectSecurityStandardsState,
  (state): LookupTable<SecurityStandard> => get(state, 'status')
);

export const selectOrderedSecurityStandards = createSelector(
  selectSecurityStandards,
  (standardsLookup): SecurityStandard[] => {
    const availableStandards = getAvailableStandards();
    return filter(map(availableStandards, standard => standardsLookup[standard.id]));
  }
);

export const selectSecurityStandardGroups = createSelector(
  selectOrderedSecurityStandards,
  (orderedStandards): SecurityStandardsGroupSection[] => {
    const groups = getAvailableStandardGroups();
    const groupedSections = reduce(groups, (result: SecurityStandardsGroupSection[], group: SecurityStandardGroup) => {
      const groupedStandards = filter(orderedStandards, (standard: SecurityStandard) => includes(standard.groupIds || [], group.id));
      if (groupedStandards.length > 0) {
        result.push({
          ...group,
          standards: groupedStandards
        });
      }
      return result;
    }, []);

    const standardsWithoutGroup = filter(orderedStandards, (standard: SecurityStandard) => !some(standard.groupIds || []));
    if (standardsWithoutGroup.length > 0) {
      groupedSections.push({
        id: "others",
        icon: "institution",
        standards: standardsWithoutGroup,
        fallback: true
      });
    }

    return groupedSections;
  }
);


export const makeSecurityStandardSelector = createSelector(
  selectSecurityStandards,
  (standards: LookupTable<SecurityStandard>): SecurityStandard => memoize(
    (id: string): SecurityStandard => {
      return standards[id];
    })
);



export const selectSecurityStandardGuidelinesState = createSelector(
  selectSecurityStandardsState,
  (state): ListDataState<SecurityStandardGuideline> => memoize(
    (securityStandardId) => {
      return get(state, 'subEntities.' + securityStandardId + ".guidelines")
    })
);

export const selectSecurityStandardGuidelines = createSelector(
  selectSecurityStandardGuidelinesState,
  (selectGuidelinesState): LookupTable<SecurityStandardGuideline> => memoize(
    (securityStandardId) => {
      return get(selectGuidelinesState(securityStandardId), "entities", {})
    })
);

export const selectSecurityStandardGuidelinesDataStatus = createSelector(
  selectSecurityStandardGuidelinesState,
  (selectGuidelinesState): LookupTable<SecurityStandardGuideline> => memoize(
    (securityStandardId) => {
      return get(selectGuidelinesState(securityStandardId), "status")
    })
);

export const selectAllSecurityStandardsGuidelines = createSelector(
  selectSecurityStandards,
  selectSecurityStandardGuidelines,
  (standards, getStandardGuidelines): LookupTable<SecurityStandardGuideline> =>
    reduce(standards, (result, standard) => {
      return {
        ...getStandardGuidelines(standard.id),
        ...result
      };
    }, {})
);

export const selectSecurityStandardGuidelinesRelationshipsState = createSelector(
  selectSecurityStandardsState,
  (state): ListDataState<SecurityStandardGuidelineRelationships> => memoize(
    (securityStandardId) => {
      return get(state, 'subEntities.' + securityStandardId + ".guidelinesRelationships")
    })
);

export const selectSecurityStandardGuidelinesRelationships = createSelector(
  selectSecurityStandardGuidelinesRelationshipsState,
  (selectGuidelinesState): LookupTable<SecurityStandardGuidelineRelationships> => memoize(
    (securityStandardId) => {
      return get(selectGuidelinesState(securityStandardId), "entities", {})
    })
);


export const selectSecurityStandardRootGuidelines = createSelector(
  selectSecurityStandardGuidelines,
  (selectGuidelines): LookupTable<SecurityStandardGuideline> => memoize(
    (securityStandardId) => {
      const guidelines = selectGuidelines(securityStandardId);
      const roots = filter(guidelines, guideline => {
        return !find(guidelines,
          (otherGuideline: SecurityStandardGuideline) => isGuidelineWithSubGuidelines(otherGuideline) && includes(otherGuideline.subGuidelines, guideline.id))
      });

      return listToLookupTable(roots);
    })
);


export const makeSecurityStandardGuidelineSelector = createSelector(
  selectAllSecurityStandardsGuidelines,
  (guidelines: LookupTable<SecurityStandardGuideline>) => memoize(
    (id: string): SecurityStandardGuideline => {
      return guidelines[id];
    })
);

export const selectSecurityStandardVariantsState = createSelector(
  selectSecurityStandardsState,
  (state): ListDataState<SecurityStandardVariant> => memoize(
    (securityStandardId) => {
      return get(state, 'subEntities.' + securityStandardId + ".variants")
    })
);

export const selectSecurityStandardVariants = createSelector(
  selectSecurityStandardVariantsState,
  (selectVariantsState): LookupTable<SecurityStandardVariant> => memoize(
    (securityStandardId) => {
      return get(selectVariantsState(securityStandardId), "entities")
    })
);

export const selectSecurityStandardVariantsDataStatus = createSelector(
  selectSecurityStandardVariantsState,
  (selectVariantsState): DataStatus => memoize(
    (securityStandardId) => {
      return get(selectVariantsState(securityStandardId), "status")
    })
);


